/**
 * WO#2 — Live territory adapter (PURE).
 *
 * Transforms authoritative API parcels into WO#1 `PropertyUnit`s and resolves
 * the single continuous territory that contains a selected parcel — using the
 * accepted WO#1 domain as the ONLY mathematical authority.
 *
 * No new coordinate system, no bounding-box coordinates, no geometry rewrite,
 * no merging of ownership units, no mutation of the source objects. Tier is
 * NOT derived here (no duplicated zone rules); if present it is passed through
 * from the canonical zone-classification authority by the caller.
 */
import type { ApiParcel } from "../lib/parcels-by-owner";
import {
  buildTerritories,
  normalizeOwnerIdentity,
  type PropertyUnit,
  type Territory,
} from "./territory";

/** Map one authoritative API parcel to a WO#1 PropertyUnit (source untouched). */
export function apiParcelToPropertyUnit(p: ApiParcel, tier?: string): PropertyUnit {
  const unit: PropertyUnit = {
    id: String(p.id),
    owner: p.owner,
    x: p.x,
    y: p.y,
    w: p.w,
    h: p.h,
  };
  if (tier !== undefined) unit.tier = tier;
  return unit;
}

/** Map a list of API parcels to PropertyUnits (no mutation of the input). */
export function apiParcelsToPropertyUnits(
  parcels: readonly ApiParcel[],
): PropertyUnit[] {
  return parcels.map((p) => apiParcelToPropertyUnit(p));
}

/**
 * Resolve the continuous same-wallet territory that contains the parcel with
 * `selectedId` owned by `owner`, from the COMPLETE authoritative owner list.
 * Returns null if the selected parcel is not present in the resolved set
 * (e.g. indexer lag) — callers must treat null as UNAVAILABLE.
 */
export function resolveTerritoryForParcel(
  ownerParcels: readonly ApiParcel[],
  selectedId: number | string,
  owner: string,
): Territory | null {
  const units = apiParcelsToPropertyUnits(ownerParcels);
  const territories = buildTerritories(units);
  const ownerKey = normalizeOwnerIdentity(owner);
  const pid = String(selectedId);
  return (
    territories.find(
      (t) => t.owner === ownerKey && t.units.some((u) => u.id === pid),
    ) ?? null
  );
}
